import { useState } from "react";
import { ChevronDown, ChevronUp, GripVertical, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { moverItemNaLista } from "../domain/mover-item-na-lista";

type Props = {
  readonly arquivos: readonly File[];
  readonly onMudar: (proxima: File[]) => void;
};

/**
 * Lista de midias com ordem editavel (arrastar ou setas).
 */
export function ListaMidiasOrdenavel({ arquivos, onMudar }: Props) {
  const [arrastandoDe, setArrastandoDe] = useState<number | null>(null);
  const [sobre, setSobre] = useState<number | null>(null);

  if (arquivos.length === 0) return null;

  function mover(de: number, para: number) {
    onMudar(moverItemNaLista(arquivos, de, para));
  }

  return (
    <ul className="grid min-w-0 gap-1.5" aria-label="Ordem das mídias">
      {arquivos.map((arquivo, indice) => (
        <li
          key={`${arquivo.name}-${arquivo.size}-${arquivo.lastModified}-${indice}`}
          draggable
          onDragStart={() => setArrastandoDe(indice)}
          onDragEnd={() => {
            setArrastandoDe(null);
            setSobre(null);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (sobre !== indice) setSobre(indice);
          }}
          onDrop={(e) => {
            e.preventDefault();
            if (arrastandoDe === null) return;
            mover(arrastandoDe, indice);
            setArrastandoDe(null);
            setSobre(null);
          }}
          className={cn(
            "flex min-w-0 items-center gap-2 rounded-lg border border-border bg-muted/40 px-2 py-1.5 text-sm text-foreground",
            "cursor-grab active:cursor-grabbing",
            sobre === indice && arrastandoDe !== null && arrastandoDe !== indice
              ? "border-ring bg-accent/50"
              : null,
            arrastandoDe === indice ? "opacity-60" : null,
          )}
        >
          <span
            className="grid size-7 shrink-0 place-items-center text-muted-foreground"
            aria-hidden
          >
            <GripVertical className="size-4" />
          </span>
          <span className="w-5 shrink-0 text-center text-xs tabular-nums text-muted-foreground">
            {indice + 1}
          </span>
          <span className="min-w-0 flex-1 truncate">{arquivo.name}</span>
          <div className="flex shrink-0 items-center gap-0.5">
            <button
              type="button"
              aria-label={`Subir ${arquivo.name}`}
              disabled={indice === 0}
              onClick={() => mover(indice, indice - 1)}
              className="grid size-7 cursor-pointer place-items-center rounded-md text-muted-foreground transition-colors hover:bg-background hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronUp className="size-4" aria-hidden />
            </button>
            <button
              type="button"
              aria-label={`Descer ${arquivo.name}`}
              disabled={indice === arquivos.length - 1}
              onClick={() => mover(indice, indice + 1)}
              className="grid size-7 cursor-pointer place-items-center rounded-md text-muted-foreground transition-colors hover:bg-background hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronDown className="size-4" aria-hidden />
            </button>
            <button
              type="button"
              aria-label={`Remover ${arquivo.name}`}
              onClick={() =>
                onMudar(arquivos.filter((_, i) => i !== indice))
              }
              className="grid size-7 cursor-pointer place-items-center rounded-md text-muted-foreground transition-colors hover:bg-background hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-3.5" aria-hidden />
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}
